import React, { useEffect, useState } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs';
import { Tab, TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import { useMedicalContext } from 'contexts/MedicalContext';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT
} from 'routes/RouteList';
import useTabIndex from 'hooks/utils/useTabIndex';
import { getLocalDateFormat } from 'utils/date';
import PersonIllness from 'models/PersonIllness';
import IllnessDetailsTab from 'containers/pages/rehab/illness/IllnessDetailsTab';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import usePerson from 'hooks/queries/usePerson';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';

function Illness() {
  const { push } = useHistory();

  const { entityId, groupId, personId, illnessId } = useParams();

  const { fetchPerson } = usePerson({ personId: personId });
  const person = useEntityContext(s => s.person);
  const { medical } = useMedicalContext();
  const [personIllness, setPersonIllness] = useState(new PersonIllness({}));
  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL
  ]);
  const [tabIndex, setTabIndex] = useTabIndex([
    [
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT
    ]
  ]);
  const edit = useRouteMatch(
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT
  )?.isExact;

  useEffect(() => {
    const fetchPersonData = async () => {
      await fetchPerson(personId);
    };
    if (!person.id) {
      fetchPersonData();
    }
  }, [entityId, personId]);

  useEffect(() => {
    if (medical?.injuries) {
      const personIllness = medical.illnesses.find(i => i.id === illnessId);
      if (personIllness) {
        setPersonIllness(
          new PersonIllness({
            ...personIllness
          })
        );
      } else {
        push(
          generatePath(ROUTE_PERSONS_ENTITY_PERSON_MEDICAL, {
            entityId,
            groupId,
            personId
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medical, illnessId]);

  return (
    <Panel active={active.isExact}>
      {person && personIllness && (
        <>
          <PanelHeader>
            <Breadcrumbs>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY, { entityId, groupId })}
              >
                <FormattedMessage {...messages.breadcrumbSporters} />
              </Breadcrumb>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
                  entityId,
                  groupId,
                  personId
                })}
              >
                {`${person.firstname} ${person.lastname}`}
              </Breadcrumb>
            </Breadcrumbs>
            <PanelTitleWrapper>
              <PanelTitle>{personIllness.title}</PanelTitle>
              <PanelSubtitle>
                {getLocalDateFormat(personIllness.injuryDate)}
                {personIllness.injuryEndDate && (
                  <>
                    {' - '}
                    {getLocalDateFormat(personIllness.injuryEndDate)}
                  </>
                )}
              </PanelSubtitle>
            </PanelTitleWrapper>
            <PanelTitleButtonsGroup>
              <ActionButton />
              <ContextMenuWrapper trigger={<PanelTitleButton />}>
                <ContextMenu />
              </ContextMenuWrapper>
            </PanelTitleButtonsGroup>
          </PanelHeader>
          <PanelBody>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={index => setTabIndex(index)}
            >
              <TabList>
                <Tab key="illness.detail">
                  <FormattedMessage {...messages.rehabIllnessDetailsTab} />
                </Tab>
              </TabList>
              <TabPanel key="illness.detail">
                <TabPanelBody withScroller>
                  <IllnessDetailsTab
                    entityId={entityId}
                    personIllness={personIllness}
                    edit={edit}
                    medical={medical}
                    personId={personId}
                  />
                </TabPanelBody>
              </TabPanel>
            </Tabs>
          </PanelBody>
        </>
      )}
    </Panel>
  );
}

export default Illness;
